import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, handleApiError } from '@/lib/api';

export default function SetupPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token || !email) {
      setError('Invalid setup link. Please ask your team admin to resend the invitation.');
      return;
    }

    setLoading(true);

    try {
      await api.post('/api/v1/auth/reset-password', {
        token,
        email,
        password,
      });
      setSuccess(true);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow dark:bg-gray-800">
        <div>
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white">Set Up Your Account</h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            You've been invited to a workspace. Choose a password to get started.
          </p>
        </div>

        {success ? (
          <div className="space-y-4">
            <div className="bg-green-50 text-green-700 p-4 rounded dark:bg-green-950/30 dark:border dark:border-green-800 dark:text-green-400">
              <p className="font-medium">Account set up successfully!</p>
              <p className="text-sm mt-1">You can now sign in and access your workspace.</p>
            </div>
            <div className="text-center">
              <Link
                to="/login"
                className="inline-block w-full py-2 px-4 bg-brand-600 hover:bg-brand-700 text-white rounded-md text-center"
              >
                Sign in
              </Link>
            </div>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded dark:bg-red-950/30 dark:border dark:border-red-800">{error}</div>
            )}
            <div className="space-y-4">
              <div>
                <label htmlFor="email-display" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email
                </label>
                <input
                  id="email-display"
                  type="email"
                  disabled
                  value={email}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  placeholder="Min 8 chars, mixed case, number, special char"
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Password must be at least 8 characters with uppercase, lowercase, number, and special character.
            </p>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 bg-brand-600 hover:bg-brand-700 text-white rounded-md disabled:opacity-50"
            >
              {loading ? 'Setting up...' : 'Set Password & Join'}
            </button>
            <div className="text-center text-sm text-gray-500 dark:text-gray-400">
              Already have an account?{' '}
              <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">
                Sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
